/**
 * Spinner — shared types, Zod prop schema, and tokens.
 *
 * The single canonical loading indicator: a circular arc whose stroke fades
 * from fully opaque (the tone base colour) to fully transparent, rotating
 * continuously. `tone` selects the gradient base colour for the surface it
 * sits on (light surface vs dark surface) and `size` sets the diameter; the
 * ring stroke scales proportionally from the diameter.
 *
 * Single source of truth for every colour/size/duration value — the platform
 * renderers hold no raw hex or pixel literals (ADR-012).
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw (nodes 5227-18045
 * default / 5227-18047 inverted)
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { duration } from '../../elements/animations';
import { sizing } from '../../elements/sizing';

export const SpinnerTone = z.enum(['default', 'inverted']);
export type SpinnerTone = z.infer<typeof SpinnerTone>;

export const spinnerPropsSchema = z.object({
  /** Gradient base colour by surface relationship; defaults to `default`. */
  tone: SpinnerTone.default('default'),
  /** Overall diameter in px; the ring stroke scales from it. Defaults to 24. */
  size: z.number().positive().optional(),
  /**
   * Accessible name for the busy/progressbar role. Optional — omit when a
   * sibling label already announces the loading state (the spinner then has no
   * competing name). No built-in default so the string stays caller-authored
   * (ADR-017), not a hard-coded literal.
   */
  accessibilityLabel: z.string().optional(),
  testID: z.string().optional(),
});

export type SpinnerProps = z.infer<typeof spinnerPropsSchema>;

/** Default diameter — the small icon token (24px). */
export const SPINNER_DEFAULT_SIZE = sizing.icon.sm;

/**
 * Ring stroke as a fraction of the diameter — a single ratio so the arc reads
 * consistently at every size (24 → 2, 16 → ~1.33, 12 → 1).
 */
export const SPINNER_STROKE_RATIO = 1 / 12;

/** Fraction of the circle the visible arc spans; the remainder is the transparent gap. */
export const SPINNER_ARC_FRACTION = 0.75;

/** One full revolution duration (ms). */
export const SPINNER_DURATION_MS = duration.spin;

/** Proportional ring stroke width for a given diameter. */
export function spinnerStrokeFor(size: number): number {
  return size * SPINNER_STROKE_RATIO;
}

/**
 * Gradient base colour per tone: `default` sits on light surfaces (dark
 * gradient), `inverted` sits on dark surfaces (light/white gradient).
 */
export function spinnerGradientBaseFor(tone: SpinnerTone): string {
  return tone === 'inverted' ? colors.background.light : colors.background.dark;
}

/** Geometry for a given diameter — shared by both platform renderers. */
export interface SpinnerGeometry {
  size: number;
  stroke: number;
  radius: number;
  center: number;
  circumference: number;
  arcLength: number;
  gapLength: number;
}

export function spinnerGeometry(size: number): SpinnerGeometry {
  const stroke = spinnerStrokeFor(size);
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  return {
    size,
    stroke,
    radius,
    center: size / 2,
    circumference,
    arcLength: circumference * SPINNER_ARC_FRACTION,
    gapLength: circumference * (1 - SPINNER_ARC_FRACTION),
  };
}
